import { ArrowLeft } from "lucide-react";
import { useRootStore } from "@/state/RootStore";
import { useNavigate } from "react-router";
import { observer } from "mobx-react";
import { BasePage } from "../BasePage";
import moment from "moment";
import { useCallback } from "react";
import { createPortalSession } from "@/api/fetch";
import { APP_TO_BUILD } from "../../../appsConfig";
import { useTranslation } from "react-i18next";
import { formatLong } from "@/utils/date";
import { getCurrentLanguage } from "@/i18n";
import { useIsMobileWeb } from "@/hooks/useIsMobileWeb";
import DownloadAppCta, {
  DOWNLOAD_APP_CTA_CLEARANCE_CLASS,
} from "@/components/mobile/DownloadAppCta";
import { cn } from "@/lib/utils";

// Type definitions
type ProductSource = "stripe" | "app_store" | "play_store";
type ProductType = "one-time" | "subscription";

interface PurchasedProduct {
  sku: string;
  humanReadable: string;
  // The store is identified by `source`. There is deliberately no `platform`
  // field: the backend never sends one, and declaring it as required made
  // TypeScript vouch for a value that was always undefined at runtime.
  source: ProductSource;
  type: ProductType;
  purchaseDate?: number | string;
  expiryDate?: number | string;
}

interface PortalSessionResponse {
  url: string;
}

const ManagePayments = observer(() => {
  const { t } = useTranslation();
  const store = useRootStore();
  const navigate = useNavigate();
  const isMobileWeb = useIsMobileWeb();

  const isValidNumber = (value: any): value is number => {
    return typeof value === "number" && !isNaN(value) && isFinite(value);
  };

  const formatDate = (date: number | string | undefined): string => {
    if (!date) return "-";

    const numDate = Number(date);

    if (!isValidNumber(numDate)) return "-";

    // Convert to milliseconds if timestamp is in seconds
    const timestamp = numDate > 10000000000 ? numDate : numDate * 1000;

    return formatLong(new Date(timestamp), getCurrentLanguage());
  };

  const handleOpenPortal = useCallback(async (): Promise<void> => {
    try {
      const currentUrl = window.location.origin;
      const link =
        import.meta.env.DEV || currentUrl.includes("localhost:")
          ? `${currentUrl}/${APP_TO_BUILD}`
          : currentUrl;

      const response = (await createPortalSession({
        returnUrl: `${link}/profile/manage-payments`,
      })) as PortalSessionResponse;

      if (response.url) {
        window.location.href = response.url;
      }
    } catch (error) {
      console.error("Failed to open portal:", error);
    }
  }, []);

  const getManagedByText = (product: PurchasedProduct): string => {
    if (product.source === "stripe") {
      return t("payments.managed_by_stripe");
    }

    const deviceText = t("payments.manage_via_mobile");
    // Branch on `source`, which the backend actually sends. Reading the absent
    // `platform` field made this ternary always fall through, telling every
    // App Store purchaser to manage their subscription in Google Play.
    return product.source === "app_store"
      ? t("payments.managed_by_app_store", { deviceText })
      : t("payments.managed_by_google_play", { deviceText });
  };

  const getProductTypeText = (type: ProductType): string => {
    return type === "one-time"
      ? t("payments.one_time_purchase")
      : t("payments.subscription");
  };

  const isClickable = (product: PurchasedProduct): boolean => {
    return product.source === "stripe";
  };

  const handleProductClick = (product: PurchasedProduct): void => {
    if (isClickable(product)) {
      handleOpenPortal();
    }
  };

  const purchasedProducts = store.mainAppStore.newPurchaseState
    ?.purchasedProducts as PurchasedProduct[] | undefined;

  const content = (
    <div
      className={cn(
        "flex flex-col gap-6",
        isMobileWeb
          ? `w-full max-w-[594px] px-6 py-8 ${DOWNLOAD_APP_CTA_CLEARANCE_CLASS}`
          : "grow pt-5 px-10"
      )}
    >
          <button
            onClick={() => navigate("/profile")}
            className="flex gap-4 pb-5 items-center"
            aria-label={t("profile.back_to_profile_aria")}
          >
            <ArrowLeft />
            <h2 className="text-3xl font-semibold text-[#232323]">
              {t("payments.page_title")}
            </h2>
          </button>

      {!purchasedProducts || purchasedProducts.length === 0 ? (
          <div className="text-center text-gray-500 py-10">
            {t("payments.no_subscriptions")}
          </div>
      ) : (
        purchasedProducts.map((product) => {
          const showPurchaseDate =
            product.purchaseDate !== undefined &&
            isValidNumber(Number(product.purchaseDate));

          const showExpiryDate =
            product.type === "subscription" &&
            product.expiryDate !== undefined &&
            isValidNumber(Number(product.expiryDate));

          return (
            <button
              onClick={() => handleProductClick(product)}
              key={product.sku}
              className={`bg-white text-left rounded-xl shadow-lg p-6 border border-gray-200 ${
                isClickable(product)
                  ? "cursor-pointer hover:shadow-xl transition-shadow"
                  : "cursor-default"
              }`}
              type="button"
            >
              <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between mb-4">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">
                    {product.humanReadable}
                  </h2>

                  {showPurchaseDate && (
                    <p className="text-[#5B616D] mt-1">
                      {t("payments.start_date_with_value", {
                        date: formatDate(product.purchaseDate),
                      })}
                    </p>
                  )}

                  {showExpiryDate && (
                    <p className="text-[#5B616D] mt-1">
                      {t("payments.expiry_date_with_value", {
                        date: formatDate(product.expiryDate),
                      })}
                    </p>
                  )}
                </div>

                <div className="flex flex-col items-start sm:items-end">
                  <span className="w-fit bg-[#43B0471A] text-[#43B047] px-3 py-1 rounded-full text-sm font-medium">
                    {t("payments.active")}
                  </span>
                  <span className="text-[#5B616D]">
                    {getProductTypeText(product.type)}
                  </span>
                </div>
              </div>

              <div className="border-t border-gray-200 pt-4">
                <p className="text-[#5B616D]">{getManagedByText(product)}</p>
              </div>
            </button>
          );
        })
      )}
      </div>
  );

  if (isMobileWeb) {
    return (
      <div className="min-h-full bg-white flex justify-center">
        {content}
        <DownloadAppCta />
      </div>
    );
  }

  return (
    <BasePage activeItem="profile">
      {content}
    </BasePage>
  );
});

export default ManagePayments;
